/**
 * useCablePathsStandardized Hook
 *
 * Standardized hook for cable path CRUD operations.
 * Wraps useEntity with path-specific logic including length calculations.
 */

import { useMemo, useCallback } from 'react';
import { useEntity } from '@/hooks/queries/useEntityQuery';
import { cablePathServiceAdapter } from '@/services/adapters/cablePathServiceAdapter';
import {
  calculatePathRealLength,
  getFullPathPoints,
  aggregateCableRequirements,
} from '@/services/cablePathService';
import type {
  CablePath,
  CableEntry,
  ConduitConfig,
  PathType,
  PathRoute,
  CableRequirement,
} from '@/types/cablePaths';
import type { Point } from '@/types/common';
import type { ScaleData } from '@/types/floorPlanViewer';

export function useCablePathsStandardized(
  floorPlanId: string,
  junctionCoordMap?: Map<string, { x: number; y: number }>,
  scaleData?: ScaleData | null,
) {
  const {
    data,
    isLoading,
    error,
    refetch,
    create,
    update,
    delete: deletePath,
    ...mutations
  } = useEntity<CablePath>('cable_paths', floorPlanId, cablePathServiceAdapter);

  /**
   * Create a new cable path.
   */
  const createPath = useCallback(
    async (pathData: Partial<CablePath>, options?: { showSuccessToast?: boolean }) => {
      const pathWithFloorPlan = {
        ...pathData,
        floor_plan_id: floorPlanId,
      };
      return create(pathWithFloorPlan, options);
    },
    [floorPlanId, create],
  );

  /**
   * Update a path's properties.
   */
  const updatePath = useCallback(
    async (id: string, updates: Partial<CablePath>) => {
      return update({ id, updates, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Update path geometry (points).
   */
  const updatePathGeometry = useCallback(
    async (
      id: string,
      points: Point[],
      startJunctionId?: string | null,
      endJunctionId?: string | null,
    ) => {
      const updates: Partial<CablePath> = { points };
      if (startJunctionId !== undefined) updates.start_junction_id = startJunctionId;
      if (endJunctionId !== undefined) updates.end_junction_id = endJunctionId;

      // Recalculate length if we have scale data
      if (scaleData && junctionCoordMap) {
        const startJunction = startJunctionId ? junctionCoordMap.get(startJunctionId) : null;
        const endJunction = endJunctionId ? junctionCoordMap.get(endJunctionId) : null;
        updates.length_real = calculatePathRealLength(
          points,
          startJunction,
          endJunction,
          scaleData,
        );
        updates.length_unit = scaleData.unit;
      }

      return update({ id, updates, showSuccessToast: false });
    },
    [update, junctionCoordMap, scaleData],
  );

  /**
   * Update path type (conduit vs cable-only).
   */
  const updatePathType = useCallback(
    async (id: string, pathType: PathType, conduit?: ConduitConfig | null) => {
      const updates: Partial<CablePath> = { path_type: pathType };
      if (pathType === 'cable_only') {
        updates.conduit = null;
      } else if (conduit) {
        updates.conduit = conduit;
      }
      return update({ id, updates, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Update conduit configuration.
   */
  const updateConduit = useCallback(
    async (id: string, conduit: ConduitConfig) => {
      return update({ id, updates: { conduit }, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Update route (ceiling/floor/wall).
   */
  const updateRoute = useCallback(
    async (id: string, route: PathRoute) => {
      return update({ id, updates: { route }, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Update cables array.
   */
  const updateCables = useCallback(
    async (id: string, cables: CableEntry[]) => {
      return update({ id, updates: { cables }, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Add a cable to a path.
   */
  const addCable = useCallback(
    async (id: string, cable: CableEntry) => {
      const path = data?.find(p => p.id === id);
      if (!path) throw new Error('Path not found');

      const updatedCables = [...path.cables, cable];
      return update({ id, updates: { cables: updatedCables }, showSuccessToast: false });
    },
    [data, update],
  );

  /**
   * Remove a cable from a path by index.
   */
  const removeCable = useCallback(
    async (id: string, cableIndex: number) => {
      const path = data?.find(p => p.id === id);
      if (!path) throw new Error('Path not found');

      const updatedCables = path.cables.filter((_, i) => i !== cableIndex);
      return update({ id, updates: { cables: updatedCables }, showSuccessToast: false });
    },
    [data, update],
  );

  /**
   * Update vertical drop settings.
   */
  const updateVerticalDrops = useCallback(
    async (id: string, start: number, end: number, unit?: string) => {
      const updates: Partial<CablePath> = {
        vertical_drop_start: start,
        vertical_drop_end: end,
      };
      if (unit) updates.vertical_drop_unit = unit;
      return update({ id, updates, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Delete a path.
   */
  const safeDeletePath = useCallback(
    async (id: string) => {
      return deletePath(id);
    },
    [deletePath],
  );

  /**
   * Get a path by ID from cached data.
   */
  const getPathById = useCallback(
    (id: string): CablePath | undefined => {
      return data?.find(path => path.id === id);
    },
    [data],
  );

  /**
   * Get paths connected to a specific junction.
   */
  const getPathsForJunction = useCallback(
    (junctionId: string): CablePath[] => {
      if (!data) return [];
      return data.filter(
        path => path.start_junction_id === junctionId || path.end_junction_id === junctionId,
      );
    },
    [data],
  );

  /**
   * Get full points for a path (including junction coordinates).
   */
  const getFullPoints = useCallback(
    (pathId: string): Point[] => {
      const path = data?.find(p => p.id === pathId);
      if (!path || !junctionCoordMap) return [];
      return getFullPathPoints(path, junctionCoordMap);
    },
    [data, junctionCoordMap],
  );

  /**
   * Calculate real-world length for a path.
   */
  const calculateLength = useCallback(
    (pathId: string): number => {
      const path = data?.find(p => p.id === pathId);
      if (!path || !junctionCoordMap || !scaleData) return 0;

      const startJunction = path.start_junction_id
        ? junctionCoordMap.get(path.start_junction_id)
        : null;
      const endJunction = path.end_junction_id ? junctionCoordMap.get(path.end_junction_id) : null;

      return calculatePathRealLength(path.points, startJunction, endJunction, scaleData);
    },
    [data, junctionCoordMap, scaleData],
  );

  /**
   * Aggregate cable requirements for reporting.
   */
  const getCableRequirements = useCallback((): CableRequirement[] => {
    if (!data || !junctionCoordMap) return [];
    return aggregateCableRequirements(data, junctionCoordMap, scaleData ?? null);
  }, [data, junctionCoordMap, scaleData]);

  /**
   * Statistics about paths.
   */
  const statistics = useMemo(() => {
    if (!data)
      return {
        total: 0,
        conduitPaths: 0,
        cableOnlyPaths: 0,
        totalLength: 0,
        byRoute: { ceiling: 0, floor: 0, wall: 0 },
      };

    const conduitPaths = data.filter(p => p.path_type === 'conduit').length;
    const cableOnlyPaths = data.filter(p => p.path_type === 'cable_only').length;

    // Calculate total length if we have scale data
    let totalLength = 0;
    if (junctionCoordMap && scaleData) {
      for (const path of data) {
        const startJunction = path.start_junction_id
          ? junctionCoordMap.get(path.start_junction_id)
          : null;
        const endJunction = path.end_junction_id
          ? junctionCoordMap.get(path.end_junction_id)
          : null;
        totalLength += calculatePathRealLength(path.points, startJunction, endJunction, scaleData);
      }
    }

    const byRoute = {
      ceiling: data.filter(p => p.route === 'ceiling').length,
      floor: data.filter(p => p.route === 'floor').length,
      wall: data.filter(p => p.route === 'wall').length,
    };

    return {
      total: data.length,
      conduitPaths,
      cableOnlyPaths,
      totalLength,
      byRoute,
    };
  }, [data, junctionCoordMap, scaleData]);

  return {
    // Data
    paths: data ?? [],

    // Loading states
    isLoading,
    error,

    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,

    // CRUD Operations
    createPath,
    updatePath,
    deletePath: safeDeletePath,
    refetch,

    // Geometry operations
    updatePathGeometry,

    // Configuration operations
    updatePathType,
    updateConduit,
    updateRoute,
    updateCables,
    addCable,
    removeCable,
    updateVerticalDrops,

    // Query utilities
    getPathById,
    getPathsForJunction,
    getFullPoints,
    calculateLength,

    // Reporting
    getCableRequirements,

    // Statistics
    statistics,
  };
}
